import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Check, X } from "lucide-react";
import { useCart } from "../../lib/cart";
import { pickLocale } from "../../lib/format";
import { useI18n } from "../../lib/i18n";

/**
 * A page-level "added to cart" confirmation.
 *
 * The product card's own checkmark was too quiet — shoppers pressed a size,
 * saw almost nothing, and thought it had not worked. This announces every
 * successful add, from the card or the product page, with a link straight to
 * the basket. Auto-dismisses; a new add resets the timer.
 */
export default function CartToast() {
  const { toast, dismissToast, itemCount } = useCart() || {};
  const { t, locale } = useI18n();
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!toast?.at) return undefined;
    setVisible(true);
    const timer = setTimeout(() => setVisible(false), 3800);
    return () => clearTimeout(timer);
  }, [toast?.at]);

  if (!toast?.at) return null;
  // PDP öz cavabını CTA-nın özündə verir ("Səbətdə ✓") — üstündən toast atmaq
  // eyni xəbəri iki dəfə deməkdir. Kartlardan əlavə (listinq, look) toast qalır.
  if (pathname.startsWith("/products/")) return null;

  const title = pickLocale(toast.product_title, locale) || t("cart.toast.generic", "Məhsul");
  const variant = toast.variant_title;

  return (
    <div
      aria-live="polite"
      className={`fixed left-1/2 z-[60] w-[calc(100%-2rem)] max-w-[420px] -translate-x-1/2 transition-all duration-300 ${
        visible ? "bottom-6 opacity-100" : "-bottom-24 opacity-0"
      }`}
      data-testid="cart-toast"
    >
      <div className="flex items-center gap-3 border border-[var(--adf-gray-5)] bg-[var(--adf-ink)] px-4 py-3 text-[var(--adf-white-warm,#fff)] shadow-xl">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15">
          <Check size={18} strokeWidth={2.2} aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-medium">{title}</div>
          <div className="text-[12px] text-white/70">
            {variant ? `${variant} · ` : ""}
            {t("cart.toast.added", "səbətə əlavə edildi")}
            {itemCount ? ` · ${t("cart.toast.count", "səbətdə {n}", { n: itemCount })}` : ""}
          </div>
        </div>
        <Link
          to="/cart"
          onClick={() => { setVisible(false); dismissToast?.(); }}
          className="shrink-0 whitespace-nowrap border border-white/40 px-3 py-1.5 text-[12px] uppercase tracking-[0.1em] transition hover:bg-white hover:text-[var(--adf-ink)]"
          data-testid="cart-toast-view"
        >
          {t("cart.toast.view", "Səbətə bax")}
        </Link>
        <button
          type="button"
          aria-label={t("common.close", "Bağla")}
          onClick={() => { setVisible(false); dismissToast?.(); }}
          className="shrink-0 text-white/60 transition hover:text-white"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
